import React from 'react'
import { useLocation } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import maluLogo from '../../../assets/images/malu-white.png'
import { APP_GIT_SHA, APP_VERSION } from '../../../config/appVersion.config'
import { AppBar, Drawer, DrawerHeader, DrawerVersionFooter, StaffHeaderActionsContainer, StaffHeaderLogo } from './Drawer.styled'
import DrawerMenuItems from './components/DrawerMenuItems'
import StaffAccountMenu from './components/StaffAccountMenu'
import StaffLanguageMenu from './components/StaffLanguageMenu'
import { TitleText } from '../../styles/Typography.styled'
import { useIsMobile } from '../../../hooks/useScreenSize'
import { userAtom } from '../../../stores/userAtom'
import { getUserDisplayName } from '../../../utils/userDisplay.utils'

import type { MenuItem as StaffMenuItem } from '../../../types/menuItem.types'

const StaffHeaderActions: React.FC = () => {
  const user = useAtomValue(userAtom)
  const userDisplayName = getUserDisplayName(user)

  return (
    <StaffHeaderActionsContainer>
      <StaffAccountMenu userDisplayName={userDisplayName} />
      <StaffLanguageMenu />
    </StaffHeaderActionsContainer>
  )
}

export default function MiniDrawer({
  menuItems,
  drawerWidth,
  children,
}: {
  menuItems: StaffMenuItem[]
  drawerWidth: number
  children: React.ReactNode
}) {
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const location = useLocation()
  const isMobile = useIsMobile()
  const shouldShowAccountActions = !(open && isMobile)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const getHeaderTitle = () => {
    const currentRoute = menuItems.find((menuItem) => menuItem.url === location.pathname)
    return currentRoute ? currentRoute.label : ''
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* ------------------------------- Header -------------------------------- */}
      <AppBar color="secondary" position="fixed" open={open} drawerWidth={drawerWidth}>
        <Toolbar sx={{ position: 'relative' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={[
              {
                marginRight: 5,
                zIndex: 1,
              },
              open && { display: 'none' },
            ]}
          >
            <MenuIcon />
          </IconButton>
          <TitleText textColor="white" sx={{ zIndex: 1 }}>
            {getHeaderTitle()}
          </TitleText>
          <StaffHeaderLogo desktopOnly alt="Malu" src={maluLogo} />
          {shouldShowAccountActions && <StaffHeaderActions />}
        </Toolbar>
      </AppBar>
      {/* ------------------------------- Drawer -------------------------------- */}
      <Drawer variant="permanent" open={open} drawerWidth={drawerWidth}>
        <DrawerHeader>
          <IconButton aria-label="close drawer" onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        {/* ------------------------------- Menu Items -------------------------------- */}
        <DrawerMenuItems menuItems={menuItems} open={open} />
        {open && <DrawerVersionFooter data-git-sha={APP_GIT_SHA}>v{APP_VERSION}</DrawerVersionFooter>}
      </Drawer>
      {/* ------------------------------- Page Content -------------------------------- */}
      <Box component="main" sx={{ flexGrow: 1, p: 0, height: '100%', overflow: 'auto' }}>
        <Toolbar /> {/* This automatically takes the header's height */}
        {children}
      </Box>
    </Box>
  )
}
